import { useEffect, useSyncExternalStore } from "react";
import { Logger } from "@cede/utils";
import pako from "pako";

const WS_URL = "wss://ws-manager-compress.bitmart.com/api?protocol=1.1";
const MAX_RECONNECT_ATTEMPTS = 10;
const MAX_RECONNECT_DELAY = 30_000;
const PING_INTERVAL = 15_000; // BitMart recommends sending "ping" every 15s to keep the connection alive

// --- Module-level singleton managing a single shared WebSocket connection ---

type SetupFn = (ws: WebSocket) => (() => void) | void;
type MessageListener = (data: unknown) => void;
type ErrorListener = (error: string) => void;
type StatusListener = () => void;

interface Subscriber {
  setup: SetupFn;
  onMessage?: MessageListener;
  onError?: ErrorListener;
  cleanup: (() => void) | null;
}

let ws: WebSocket | null = null;
let isConnecting = false;
let isConnected = false;
let connectionError: string | null = null;
let reconnectAttempts = 0;
let reconnectTimeout: NodeJS.Timeout | undefined;
let disconnectTimeout: NodeJS.Timeout | undefined;
let pingInterval: NodeJS.Timeout | undefined;

const subscribers = new Map<symbol, Subscriber>();
const statusListeners = new Set<StatusListener>();

function notifyStatusListeners() {
  statusListeners.forEach((listener) => listener());
}

function startPing() {
  if (pingInterval) return;
  pingInterval = setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send("ping");
    }
  }, PING_INTERVAL);
}

function stopPing() {
  if (pingInterval) {
    clearInterval(pingInterval);
    pingInterval = undefined;
  }
}

function runSetup(subscriber: Subscriber) {
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  subscriber.cleanup?.();
  const cleanup = subscriber.setup(ws);
  subscriber.cleanup = cleanup ?? null;
}

function runAllSetups() {
  subscribers.forEach((subscriber) => runSetup(subscriber));
}

function cleanupAllSubscribers() {
  subscribers.forEach((subscriber) => {
    subscriber.cleanup?.();
    subscriber.cleanup = null;
  });
}

function disconnect() {
  cleanupAllSubscribers();
  stopPing();

  if (disconnectTimeout) {
    clearTimeout(disconnectTimeout);
    disconnectTimeout = undefined;
  }

  if (ws) {
    ws.close();
    ws = null;
  }

  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = undefined;
  }

  isConnecting = false;
  isConnected = false;
  reconnectAttempts = 0;
  notifyStatusListeners();
}

function connect() {
  if (isConnecting || ws || subscribers.size === 0) return;

  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = undefined;
  }

  isConnecting = true;
  connectionError = null;
  notifyStatusListeners();

  try {
    const socket = new WebSocket(WS_URL);
    socket.binaryType = "arraybuffer"; // BitMart sends zlib-deflated binary frames on the compress endpoint

    socket.onopen = () => {
      isConnected = true;
      isConnecting = false;
      reconnectAttempts = 0;

      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      notifyStatusListeners();
      startPing();
      runAllSetups();
    };

    socket.onmessage = (event) => {
      try {
        let text: string;

        if (typeof event.data === "string") {
          // Heartbeat exchanges are sent as plain text
          if (event.data === "pong") return;
          if (event.data === "ping") {
            socket.send("pong");
            return;
          }
          text = event.data;
        } else if (event.data instanceof ArrayBuffer) {
          text = pako.inflate(new Uint8Array(event.data), { to: "string" });
        } else {
          return;
        }

        const msg = JSON.parse(text);

        subscribers.forEach((subscriber) => {
          subscriber.onMessage?.(msg);
        });
      } catch {
        // Ignore parse/decompression errors for non-JSON messages
      }
    };

    socket.onerror = () => {
      if (socket !== ws) return;

      isConnecting = false;
      isConnected = false;
      const errorMsg = "WebSocket connection error";
      connectionError = errorMsg;
      notifyStatusListeners();
      subscribers.forEach((subscriber) => subscriber.onError?.(errorMsg));
    };

    socket.onclose = () => {
      // Ignore close events from stale sockets (e.g. after disconnect() already created a new one)
      if (socket !== ws) return;

      isConnecting = false;
      isConnected = false;
      ws = null;
      stopPing();
      cleanupAllSubscribers();
      notifyStatusListeners();

      // Only reconnect if there are still active subscribers
      if (subscribers.size > 0 && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), MAX_RECONNECT_DELAY);
        reconnectAttempts++;

        Logger.log(
          `[bitmartWS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
          Logger.types.INFO,
        );

        reconnectTimeout = setTimeout(() => {
          connect();
        }, delay);
      } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        const errorMsg = "Failed to reconnect after multiple attempts";
        Logger.error("[bitmartWS] Max reconnection attempts reached, giving up");
        connectionError = errorMsg;
        notifyStatusListeners();
        subscribers.forEach((subscriber) => subscriber.onError?.(errorMsg));
      }
    };

    ws = socket;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : "Unknown error";
    Logger.error("[bitmartWS] Failed to create WebSocket connection:", errorMessage, err);
    isConnecting = false;
    const errorMsg = "Failed to create WebSocket connection";
    connectionError = errorMsg;
    notifyStatusListeners();
    subscribers.forEach((subscriber) => subscriber.onError?.(errorMsg));
  }
}

function subscribe(subscriber: Subscriber): symbol {
  if (disconnectTimeout) {
    clearTimeout(disconnectTimeout);
    disconnectTimeout = undefined;
  }

  const id = Symbol();
  subscribers.set(id, subscriber);

  if (isConnected && ws) {
    runSetup(subscriber);
  } else if (subscribers.size === 1) {
    connect();
  }

  return id;
}

function unsubscribe(id: symbol) {
  const subscriber = subscribers.get(id);
  if (subscriber) {
    subscriber.cleanup?.();
    subscriber.cleanup = null;
    subscribers.delete(id);
  }

  // Defer disconnect so React effect re-subscriptions can happen first
  // (React runs all cleanups before any new effects)
  if (subscribers.size === 0) {
    if (disconnectTimeout) clearTimeout(disconnectTimeout);
    disconnectTimeout = setTimeout(() => {
      disconnectTimeout = undefined;
      if (subscribers.size === 0) {
        disconnect();
      }
    }, 0);
  }
}

function getSnapshot() {
  return { isConnected, error: connectionError };
}

let cachedSnapshot = getSnapshot();

function getSnapshotStable() {
  const next = getSnapshot();
  if (next.isConnected !== cachedSnapshot.isConnected || next.error !== cachedSnapshot.error) {
    cachedSnapshot = next;
  }
  return cachedSnapshot;
}

function subscribeToStatus(listener: StatusListener) {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

let visibilityHandlerRegistered = false;

function registerVisibilityHandler() {
  if (visibilityHandlerRegistered || typeof window === "undefined") return;
  visibilityHandlerRegistered = true;

  window.addEventListener("visibilitychange", () => {
    if (subscribers.size === 0) return;

    if (document.visibilityState === "hidden") {
      disconnect();
    } else {
      connect();
    }
  });
}

// --- React hook wrapping the singleton ---

export interface BitmartWsSubscriber {
  /** Called when the WS is open (and on reconnect). Should return a cleanup/unsubscribe fn. */
  setup: SetupFn;
  /** Called for every incoming WS message (after heartbeat handling). */
  onMessage?: MessageListener;
  onError?: ErrorListener;
}

export interface BitmartWsClient {
  /** Imperative subscribe — multiplexes onto the same shared connection. Returns a tear-down fn. */
  subscribe(subscriber: BitmartWsSubscriber): () => void;
}

const sharedClient: BitmartWsClient = {
  subscribe(subscriber) {
    registerVisibilityHandler();
    const id = subscribe({
      setup: subscriber.setup,
      onMessage: subscriber.onMessage,
      onError: subscriber.onError,
      cleanup: null,
    });
    return () => unsubscribe(id);
  },
};

interface UseBitmartWsConnectionProps {
  /**
   * Optional. When provided, the hook auto-subscribes on mount with this `setup` callback
   * (called when the WS is open or reconnects, returning an unsubscribe fn).
   *
   * Omit it to use the hook purely to obtain `client` for imperative subscriptions.
   */
  setup?: SetupFn;
  /** Called for every incoming WS message (after heartbeat handling). Only meaningful with `setup`. */
  onMessage?: MessageListener;
  enabled?: boolean;
  onError?: ErrorListener;
}

interface UseBitmartWsConnectionResult {
  isConnected: boolean;
  error: string | null;
  /** Imperative client for callers (e.g. the TradingView datafeed) that subscribe outside of React. */
  client: BitmartWsClient;
}

/**
 * Single source of truth for BitMart spot WebSocket access.
 *
 * All hook instances share a single module-level WebSocket connection.
 * The connection opens when the first consumer (declarative `setup` or imperative
 * `client.subscribe`) mounts and closes when the last unmounts.
 * Heartbeat ("ping"/"pong" text frames), reconnection, and visibility handling are managed centrally.
 * Incoming binary frames are decompressed (zlib) before being passed to subscribers.
 *
 * Two ways to use it:
 * 1. Declarative — pass `setup` (and optionally `onMessage`) and the hook auto-subscribes.
 * 2. Imperative — call without args and use the returned `client.subscribe(...)` directly.
 */
export const useBitmartWsConnection = ({
  setup,
  onMessage,
  enabled = true,
  onError,
}: UseBitmartWsConnectionProps = {}): UseBitmartWsConnectionResult => {
  const { isConnected: connected, error } = useSyncExternalStore(subscribeToStatus, getSnapshotStable);

  useEffect(() => {
    if (!enabled || !setup) return;

    registerVisibilityHandler();

    const id = subscribe({
      setup,
      onMessage,
      onError,
      cleanup: null,
    });

    return () => {
      unsubscribe(id);
    };
  }, [enabled, setup, onMessage, onError]);

  return {
    isConnected: enabled && connected,
    error: enabled ? error : null,
    client: sharedClient,
  };
};
